import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Bug, X, Send, Copy, Camera, Image as ImageIcon } from "lucide-react";

const SEVERITY_COLORS = {
  low: "#5FBE7E",
  medium: "#FFB300",
  high: "#E5263F",
};

/**
 * Floating Bug Report launcher + submission dialog.
 * Visible to every authenticated user, on every page.
 */
const BugReportButton = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [sevs, setSevs] = useState(["low", "medium", "high"]);
  const [category, setCategory] = useState("Other");
  const [severity, setSeverity] = useState("medium");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [screenshotPath, setScreenshotPath] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotPath(data.path);
      toast.success("Screenshot attached");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    api
      .get("/bug-reports/categories")
      .then((r) => {
        setCats(r.data.categories || []);
        setSevs(r.data.severities || ["low", "medium", "high"]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) {
            uploadFile(f);
            e.preventDefault();
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open]);

  if (!user) return null;

  const submit = async () => {
    if (description.trim().length < 5) {
      toast.error("Please describe the issue (at least 5 chars)");
      return;
    }
    // Guard: don't fire a report while the photo is still on its way to storage.
    // Otherwise the report lands on the Bug Management page with no image,
    // which is exactly the bug the user hit.
    if (uploading) {
      toast.error("Please wait for the photo to finish uploading");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bug-reports", {
        description: description.trim(),
        category,
        severity,
        page_path: pathname,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot_path: screenshotPath || null,
      });
      toast.success("Bug report sent · thanks!");
      setOpen(false);
      setDescription("");
      setCategory("Other");
      setSeverity("medium");
      setScreenshotPath(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send report");
    } finally {
      setSubmitting(false);
    }
  };

  const copyDevSnippet = async () => {
    const snippet =
      "BUG REPORT (Footwork stock)\n" +
      "============================\n" +
      `Severity   : ${severity}\n` +
      `Category   : ${category}\n` +
      `Page       : ${pathname}\n` +
      `URL        : ${window.location.href}\n` +
      `When       : ${new Date().toISOString()}\n` +
      `User       : ${user?.name || ""} (${user?.role || ""})\n` +
      `Viewport   : ${window.innerWidth}x${window.innerHeight}\n` +
      `User agent : ${navigator.userAgent}\n\n` +
      "DESCRIPTION\n-----------\n" +
      (description || "(none yet)");
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — long-press to copy");
    }
  };

  return (
    <>
      <button
        data-testid="bug-report-fab"
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        title="Report a bug"
        className="fixed bottom-16 right-5 z-40 h-12 w-12 flex items-center justify-center shadow-lg border-2 border-black text-white hover:scale-105 active:scale-95 transition-transform"
        style={{ borderRadius: 999, background: "#E5263F" }}
      >
        <Bug size={20} strokeWidth={2.5} />
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] overflow-y-auto"
          data-testid="bug-report-overlay"
          onClick={() => setOpen(false)}
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div
              className="nb-card p-5 w-full max-w-xl my-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="bug-report-dialog"
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div>
                  <div className="label-eyebrow flex items-center gap-1.5">
                    <Bug size={12} strokeWidth={2.5} />
                    Report a bug
                  </div>
                  <h3 className="font-display text-2xl mt-1">
                    Tell us what went wrong
                  </h3>
                  <div className="label-eyebrow mt-1 text-[#607087]">
                    We auto-attach the page, your user, browser & screen size.
                  </div>
                </div>
                <button
                  data-testid="bug-report-close"
                  onClick={() => setOpen(false)}
                  className="nb-btn bg-white p-2"
                  aria-label="close"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>

              <div className="nb-card-sm p-3 mb-3 text-xs font-mono-num text-[#607087]">
                <div>
                  <b>Page:</b> {pathname}
                </div>
                <div className="truncate">
                  <b>Browser:</b> {navigator.userAgent.split(") ").pop()}
                </div>
                <div>
                  <b>Viewport:</b> {window.innerWidth}×{window.innerHeight}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label>
                  <div className="label-eyebrow mb-1">What kind of bug?</div>
                  <select
                    data-testid="bug-category-select"
                    className="nb-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {cats.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <div className="label-eyebrow mb-1">How bad is it?</div>
                  <div
                    className="flex gap-1 nb-input p-1 h-11"
                    data-testid="bug-severity-row"
                  >
                    {sevs.map((s) => (
                      <button
                        key={s}
                        data-testid={`bug-sev-${s}`}
                        type="button"
                        onClick={() => setSeverity(s)}
                        className={`flex-1 text-xs font-display rounded-lg transition-colors ${
                          severity === s ? "text-white" : "text-[#001740]"
                        }`}
                        style={
                          severity === s
                            ? { backgroundColor: SEVERITY_COLORS[s] }
                            : { backgroundColor: "transparent" }
                        }
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </label>
              </div>

              <label className="block mt-3">
                <div className="label-eyebrow mb-1">What happened?</div>
                <textarea
                  data-testid="bug-description-input"
                  className="nb-input min-h-[130px]"
                  rows={5}
                  placeholder={
                    "1. I tapped … on the …\n2. Expected: …\n3. Got: …"
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {description.length}/4000
                </div>
              </label>

              <label className="block mt-3">
                <div className="label-eyebrow mb-1">Screenshot (optional)</div>
                {uploading ? (
                  <div
                    data-testid="bug-screenshot-uploading"
                    className="h-32 w-40 rounded-xl border-2 border-dashed border-[#5B3A99]/50 bg-[#F1E7FA]/50 flex items-center justify-center text-sm text-[#5B3A99] font-semibold animate-pulse"
                  >
                    Uploading photo…
                  </div>
                ) : screenshotPath ? (
                  <div
                    className="relative inline-block"
                    data-testid="bug-screenshot-preview"
                  >
                    <img
                      src={imageUrl(screenshotPath)}
                      alt="screenshot"
                      className="h-32 rounded-xl border border-[#001740]/15 object-cover"
                    />
                    <button
                      type="button"
                      data-testid="bug-screenshot-remove"
                      onClick={() => setScreenshotPath(null)}
                      className="absolute -top-2 -right-2 bg-[#E5263F] text-white h-7 w-7 rounded-full flex items-center justify-center shadow"
                      aria-label="remove screenshot"
                    >
                      <X size={14} strokeWidth={3} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 items-center">
                    <label className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5 cursor-pointer">
                      <ImageIcon size={14} strokeWidth={2.5} />
                      {uploading ? "Uploading…" : "Choose file"}
                      <input
                        data-testid="bug-screenshot-file"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadFile(e.target.files?.[0])}
                      />
                    </label>
                    <label className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5 cursor-pointer">
                      <Camera size={14} strokeWidth={2.5} />
                      Take photo
                      <input
                        data-testid="bug-screenshot-camera"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => uploadFile(e.target.files?.[0])}
                      />
                    </label>
                    <span className="text-xs text-[#607087]">
                      …or <kbd className="px-1 py-0.5 rounded bg-white border border-[#001740]/15 text-[10px]">⌘V</kbd> to paste
                    </span>
                  </div>
                )}
              </label>

              <div className="flex justify-between items-center gap-2 mt-4 flex-wrap">
                <button
                  data-testid="bug-copy-devsnippet"
                  onClick={copyDevSnippet}
                  className="nb-btn bg-white px-3 py-2 flex items-center gap-1.5 text-sm"
                  type="button"
                >
                  <Copy size={14} strokeWidth={2.5} /> Copy for developer
                </button>
                <div className="flex gap-2">
                  <button
                    data-testid="bug-cancel"
                    onClick={() => setOpen(false)}
                    className="nb-btn bg-white px-4 py-2.5"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="bug-submit"
                    onClick={submit}
                    disabled={submitting || uploading}
                    title={uploading ? "Wait — photo still uploading" : ""}
                    className="nb-btn nb-btn-primary px-5 py-2.5 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Send size={16} strokeWidth={2.5} />
                    {submitting
                      ? "Sending..."
                      : uploading
                        ? "Uploading photo…"
                        : "Send report"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;
